import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { connectionKeys, messageKeys, profileKeys } from '../../../application/services/query-keys';
import { firstValueFrom, map } from 'rxjs';

import { Client } from '../../../api/api-clients';
import { ProfileConverter } from '../../../application/converters/profile.converter';
import { authGuardFn } from '@auth0/auth0-angular';
import { inject } from '@angular/core';
import { injectQueryClient } from '@tanstack/angular-query-experimental';
import { toAppConnections } from '../../../application/services/connections.service';

const PROFILE_STALE_TIME_MS = 60_000;

function fetchMyProfile(queryClient: ReturnType<typeof injectQueryClient>, client: Client) {
  return queryClient.fetchQuery({
    queryKey: profileKeys.me(),
    queryFn: () => firstValueFrom(client.profilesGET().pipe(map((p) => ProfileConverter.toAppModel(p)))),
    staleTime: PROFILE_STALE_TIME_MS,
  });
}

function prefetchSupportingData(queryClient: ReturnType<typeof injectQueryClient>, client: Client): void {
  queryClient.prefetchQuery({
    queryKey: connectionKeys.mine(),
    queryFn: () => firstValueFrom(client.connectionsGET().pipe(map(toAppConnections))),
    staleTime: PROFILE_STALE_TIME_MS,
  });
  queryClient.prefetchQuery({
    queryKey: messageKeys.conversations(),
    queryFn: () => firstValueFrom(client.messagesAll(undefined)),
    staleTime: PROFILE_STALE_TIME_MS,
  });
  queryClient.prefetchQuery({
    queryKey: profileKeys.views(),
    queryFn: () => firstValueFrom(client.views()),
    staleTime: PROFILE_STALE_TIME_MS,
  });
}

/**
 * Allows access to /app only when the user is authenticated AND has a profile.
 * Seeds the TanStack Query cache with the fetched profile and prefetches
 * connections and conversations in the background.
 * Redirects unauthenticated users to /auth/login and profileless users to /register.
 */
export const requireProfileGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const queryClient = injectQueryClient();
  const client = inject(Client);

  const result = await firstValueFrom(authGuardFn(route, state));

  if (!result) {
    return false;
  }

  try {
    await fetchMyProfile(queryClient, client);
    prefetchSupportingData(queryClient, client);
    return true;
  } catch {
    return router.createUrlTree(['/register']);
  }
};

/**
 * Allows access to /register only when the user is authenticated but has NO profile.
 * Redirects unauthenticated users to /auth/login and users with a profile to /app.
 */
export const requireNoProfileGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const queryClient = injectQueryClient();
  const client = inject(Client);

  const result = await firstValueFrom(authGuardFn(route, state));

  if (!result) {
    return false;
  }

  try {
    await fetchMyProfile(queryClient, client);
    return router.createUrlTree(['/app']);
  } catch {
    return true;
  }
};
